import React from "react";
import { useHistory, useParams } from "react-router";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import { useRestaurant } from "../../utils/contexts/restaurant-context";
import stripeClient from '../../logic/stripe';
import stripeFlow from '../../logic/stripe-flow';
import logmirror from '../../logic/logmirror';
import formats from '../../utils/formats';
import { useTranslation } from "react-i18next";

export default function StripeWidgetContent(props) {
    const { order } = props;

    const { type, id } = useParams();
    const history = useHistory();
    const { restaurant } = useRestaurant();

    const [error, setError] = React.useState(null);
    const [clientSecret, setClientSecret] = React.useState("");
    const [paymentRequest, setPaymentRequest] = React.useState(null);

    const stripe = useStripe();
    const elements = useElements();
    const { t } = useTranslation();

    React.useEffect(() => {
        if (!stripe) {
            return;
        }

        initStripePayment();

        async function initStripePayment() {

            // A previous payment may have been charged without the order ever
            // being sent (page killed during 3DS, network drop): recover it
            // instead of charging the customer again.
            const pending = await stripeFlow.checkPending(stripe, restaurant.id);
            if (pending.recovered) {
                trackStripeEvent("stripe_pending_recovered", { "pi": pending.paymentIntentId });
                history.replace(stripeFlow.successPath(type, id, pending.paymentIntentId));
                return;
            }
            if (pending.paymentIntentId) {
                // Charged (or expired unverified) but not auto-resendable:
                // needs manual reconciliation from the Stripe dashboard.
                trackStripeEvent("stripe_pending_mismatch", { "pi": pending.paymentIntentId, "reason": pending.reason });
            }

            // Create PaymentIntent as soon as the page loads
            const payload = {
                "amount": formats.toCents(order.order.total),
                "currency": "eur",
                "description": JSON.stringify({ ris_id: order.restaurant.id, amount: order.order.total }),
                "type": order.order.type,
            };

            const res = await stripeClient.createCheckout(payload);
            if (res) {
                stripeFlow.markPending(res.client_secret);
                setClientSecret(res.client_secret);
            } else {
                setError(t("paymentspage.stripewidget.p_4"));
            }
        }

    }, [stripe, order.order.total, order.order.type, order.restaurant.id]);

    React.useEffect(() => {
        if (!stripe || !elements || !clientSecret) {
            return;
        }

        const pr = stripe.paymentRequest({
            country: "IT",
            currency: "eur",
            total: {
                label: restaurant.name,
                amount: formats.toCents(order.order.total),
            }
        });

        pr.on("paymentmethod", async (event) => {
            const result = await stripeFlow.confirmWalletPayment(stripe, clientSecret, event.paymentMethod.id, status => event.complete(status));

            handleOutcome(result, result.message);
        });

        pr.canMakePayment().then(result => {
            if (result) {
                setPaymentRequest(pr);
            }
        });
    }, [stripe, elements, clientSecret, order.order.total, restaurant.name]);

    function goToSuccess(paymentIntentId) {
        stripeFlow.clearPending();
        history.push(stripeFlow.successPath(type, id, paymentIntentId));
    }

    function trackStripeEvent(action, metadata) {
        logmirror.mirrorLog(restaurant.id, action, { ...metadata, "order_type": type });
    }

    function handleOutcome(result, failedMessage) {
        if (result.outcome === "succeeded") {
            if (result.recovered) {
                trackStripeEvent("stripe_confirm_recovered", { "pi": result.paymentIntent.id });
            }
            setError(null);
            goToSuccess(result.paymentIntent.id);
        } else if (result.outcome === "unknown") {
            trackStripeEvent("stripe_outcome_unknown", { "pi": clientSecret.split("_secret")[0] });
            setError(t("paymentspage.stripewidget.p_5"));
        } else {
            trackStripeEvent("stripe_payment_failed", { "pi": clientSecret.split("_secret")[0], "message": result.message });
            setError(failedMessage);
        }
    }

    const cardStyle = {
        style: {
            base: {
                color: '#303238',
                fontSize: '20px',
                fontFamily: '"Nunito", sans-serif',
                fontSmoothing: 'antialiased',
                '::placeholder': {
                    color: '#CFD7DF',
                },
            },
            invalid: {
                color: '#e5424d',
                ':focus': {
                    color: '#303238',
                },
            },
        },
        showIcon: true,
    };

    const cardNumber = {
        style: {
            base: {
                color: '#303238',
                fontSize: '20px',
                fontFamily: '"Nunito", sans-serif',
                fontSmoothing: 'antialiased',
                '::placeholder': {
                    color: '#CFD7DF',
                },
            },
            invalid: {
                color: '#e5424d',
                ':focus': {
                    color: '#303238',
                },
            },
        },
        showIcon: true,
    };

    const handleChange = async (event) => {
        // Listen for changes in the CardElement
        // and display any errors as the customer types their card details
        setError(event.error ? event.error.message : "");
    };

    const handleSubmit = async ev => {
        ev.preventDefault();

        const result = await stripeFlow.confirmCardPaymentSafe(stripe, clientSecret, {
            payment_method: {
                card: elements.getElement(CardNumberElement)
            }
        });

        handleOutcome(result, `Payment failed ${result.message}`);
    };

    return (
        <div className="stripe-widget-content">
            <form className="relative p-6 py-10" onSubmit={handleSubmit}>
                <div className="pb-3">
                    <p className="text-xl">{t("paymentspage.stripewidget.p_1")}</p>
                    <div className="p-3 border bg-white rounded-lg">
                        <CardNumberElement
                            options={cardNumber}
                            onReady={() => {
                            }}
                            onChange={handleChange}
                            onBlur={() => {
                            }}
                            onFocus={() => {
                            }}
                        ></CardNumberElement>
                    </div>
                </div>

                <div className="py-3">
                    <div className="flex">
                        <div className="flex-1 mr-2">
                            <p className="text-xl">{t("paymentspage.stripewidget.p_2")}</p>
                            <div className="p-3 bg-white rounded-lg">
                                <CardExpiryElement
                                    options={cardStyle}
                                    onReady={() => {
                                    }}
                                    onChange={handleChange}
                                    onBlur={() => {
                                    }}
                                    onFocus={() => {
                                    }}
                                ></CardExpiryElement>
                            </div>
                        </div>

                        <div className="flex-1 ml-2">
                            <p className="text-xl">{t("paymentspage.stripewidget.p_3")}</p>
                            <div className="p-3 bg-white rounded-lg">
                                <CardCvcElement
                                    options={cardStyle}
                                    onReady={() => {
                                    }}
                                    onChange={handleChange}
                                    onBlur={() => {
                                    }}
                                    onFocus={() => {
                                    }}
                                ></CardCvcElement>
                            </div>
                        </div>
                    </div>
                </div>

                <p className="text-red-600">{error}</p>

                <div className="fixed bottom-0 left-0 w-full mt-8 p-4 py-8">
                    <div className="flex py-3 items-center justify-center">
                        <input type="checkbox" id="agreements" name="agreements" required />
                        <p className="text-xs pl-2">{t("formtable.lable_1.span_1")} <a target="_blank" className="underline" href="https://www.cartaidentitalimentare.com/privacy-esercizi-commerciali/" rel="noreferrer">{t("formtable.lable_1.a_1")}</a></p>
                    </div>

                    {paymentRequest && <PaymentRequestButtonElement options={{ paymentRequest }}></PaymentRequestButtonElement>}

                    <button className="w-full mt-4 py-2 rounded-full bg-mycia-green" type="submit" disabled={!stripe}>{t("paymentspage.stripewidget.button_1")}</button>
                </div>
            </form>
        </div>
    );
}
